import { Injectable } from '@angular/core';
import { ApiMidService } from './api-mid.service';
import { ApiBasicService } from './api-basic.service';
import { switchMap } from 'rxjs/operators';
import { AoxMeasurementResult } from 'src/app/interfaces/biozoom-api/aox-measurement-result';
import { HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { retryBackoff } from 'backoff-rxjs';
import { PreciseAoxMeasurementResult } from 'src/app/interfaces/biozoom-api/precise-aox-measurement-result';
import { HrvMeasurementResult } from 'src/app/interfaces/biozoom-api/hrv-measurement-result';
import { HrvTestMeasurementResult } from 'src/app/interfaces/biozoom-api/hrv-test-measurement-result';
import { ConnectionService } from './connection.service';
import { PreRequestResult } from 'src/app/interfaces/biozoom-api/pre-request-result';

@Injectable({
  providedIn: 'root'
})
/**
 * api service for high-level methods
 * these string together several base methods, add response handling, and provide additional stages like 'retryBackoff'
 */
export class ApiTopService {

  constructor(
    private apiMidService: ApiMidService,
    private apiBasicService: ApiBasicService,
    private connectionService: ConnectionService,
  ) { }

  /**
   * upload a single quick aox measurement to the server
   * initially, do a pre-request to identify which server to connect to
   * send a pre-request to each server if the previous one failed, and repeat this whole procedure up to 5 times
   * use a VWS
   * @param demoMode if true, return a mock-observable instead of performing an actual request
   * @param serial serial ID of the scanner
   * @param data measurement that is to be processed
   * @param language currently used language
   * @param additionalData any additional data the backend might require
   * @returns observable for the server response
   */
  executeMeasurementAoxVWS = (
    demoMode = false,
    serial: string,
    data: string,
    language = 'de',
    additionalData?: any,
    preRequestTimeoutMs?: number,
  ): Observable<HttpEvent<AoxMeasurementResult>> => {
    return this.apiMidService.doPrerequest(
      this.apiBasicService.getConfiguration(), serial, 0, 0, demoMode, 0, preRequestTimeoutMs
    ).pipe(
      switchMap(el => {
        return this.apiMidService.doMeasurementVWS(
          serial, data, el.waiting_time_ms, el.recommended_server, demoMode, language.substring(0, 2), additionalData
        );
      }),
      retryBackoff({
        initialInterval: 1000,
        maxRetries: 5,
      })
    );
  }

  /**
   * Resolve a VWS target for an AOX upload. This is exposed separately so
   * config-driven flows can show the routing step independently of the upload.
   */
  requestMeasurementAoxVWS = (
    demoMode = false,
    serial: string,
    preRequestTimeoutMs?: number,
  ): Observable<PreRequestResult> => {
    return this.apiMidService.doPrerequest(
      this.apiBasicService.getConfiguration(), serial, 0, 0, demoMode, 0, preRequestTimeoutMs
    );
  }

  /** Resolve an AOX route through one explicitly selected VWS routing endpoint. */
  requestMeasurementAoxVWSOnServer = (
    demoMode = false,
    serial: string,
    vwsBaseUrl: string,
    preRequestTimeoutMs?: number,
  ): Observable<PreRequestResult> => {
    return this.apiMidService.doPrerequestOnServer(
      this.apiBasicService.getConfiguration(), vwsBaseUrl, serial, 0, demoMode, preRequestTimeoutMs
    );
  }

  /** Snapshot the configured VWS routing endpoints for one transparent AOX upload flow. */
  getAoxVwsServers = (): string[] => [...(this.apiBasicService.getConfiguration().vwsBaseUrls ?? [])];

  /**
   * Upload an AOX measurement to a VWS selected by a previous pre-request.
   */
  executeMeasurementAoxVWSOnServer = (
    demoMode = false,
    serial: string,
    data: string,
    waitingTimeMs: number,
    recommendedServer: string,
    language = 'de',
    additionalData?: any
  ): Observable<HttpEvent<AoxMeasurementResult>> => {
    return this.apiMidService.doMeasurementVWS(
      serial, data, waitingTimeMs, recommendedServer, demoMode, language.substring(0, 2), additionalData
    );
  }

  /** Resolve a Precise AOX route through one explicitly selected VWS routing endpoint. */
  requestMergeAoxVWSOnServer = (
    demoMode = false,
    serial: string,
    vwsBaseUrl: string,
    preRequestTimeoutMs?: number,
  ): Observable<PreRequestResult> => {
    return this.apiMidService.doPrerequestOnServer(
      this.apiBasicService.getConfiguration(), vwsBaseUrl, serial, 1, demoMode, preRequestTimeoutMs
    );
  }

  /** Upload a Precise AOX series to a VWS selected by a previous pre-request. */
  executeMergeAoxResultsOnServer = (
    demoMode = false,
    serial: string,
    waitingTimeMs: number,
    recommendedServer: string,
    language = 'de',
    additionalData: { series_q: number[], series_results: number[] },
  ): Observable<HttpEvent<PreciseAoxMeasurementResult>> => {
    return this.apiMidService.doMeasurementVWSPrecise(
      serial, waitingTimeMs, recommendedServer, demoMode, language.substring(0, 2), additionalData
    );
  }

  /**
   * upload a single quick aox measurement to the server
   * initially, do a pre-request to identify which server to connect to
   * send a pre-request to each server if the previous one failed, and repeat this whole procedure up to 5 times
   * do not use a VWS
   * @param demoMode if true, return a mock-observable instead of performing an actual request
   * @param serial serial ID of the scanner
   * @param data measurement that is to be processed
   * @param language currently used language
   * @param additionalData any additional data the backend might require
   * @returns observable for the server response
   */
  executeMeasurementAox = (
    demoMode = false,
    serial: string,
    data: string,
    language = 'de',
    additionalData?: any
  ): Observable<HttpEvent<AoxMeasurementResult>> => {
    return this.connectionService.retryRequest(
      this.apiBasicService.doMeasurementSingle(serial, data, demoMode, language.substring(0, 2), additionalData),
      1000,
      5
    );
  }

  /** Send one direct-server AOX request; retry policy belongs to the caller. */
  executeMeasurementAoxDirectSingle = (
    demoMode = false,
    serial: string,
    data: string,
    language = 'de',
    additionalData?: any
  ): Observable<HttpEvent<AoxMeasurementResult>> => {
    return this.apiBasicService.doMeasurementSingle(serial, data, demoMode, language.substring(0, 2), additionalData);
  }

  /**
   * upload all previous aox results to the server, and merge then into a single result
   * initially, do a pre-request to identify which server to connect to
   * send a pre-request to each server if the previous one failed, and repeat this whole procedure up to 5 times
   * @param demoMode if true, return a mock-observable instead of performing an actual request
   * @param serial serial ID of the scanner
   * @param language currently used language
   * @param additionalData any additional data the backend might require, and has to include previous server responses
   * @returns observable for the server response
   */
  executeMergeAoxResults = (
    demoMode = false,
    serial: string,
    language = 'de',
    additionalData: {
      series_q: number[],
      series_results: number[]
    },
    preRequestTimeoutMs?: number,
  ): Observable<HttpEvent<PreciseAoxMeasurementResult>> => {
    return this.apiMidService.doPrerequest(
      this.apiBasicService.getConfiguration(), serial, 1, 0, demoMode, 0, preRequestTimeoutMs
    ).pipe(
      switchMap(el => {
        if (additionalData.series_q.length === 0 || additionalData.series_results.length === 0) {
          throw new Error('Not enough aox results received from server.');
        }
        return this.apiMidService.doMeasurementVWSPrecise(
          serial, el.waiting_time_ms, el.recommended_server, demoMode, language.substring(0, 2), additionalData
        );
      }),
      retryBackoff({
        initialInterval: 1000,
        maxRetries: 5,
      })
    );
  }


  /**
   * upload a hrv measurement to the server
   * attempt to send the data up to 4 times
   * @param demoMode if true, return a mock-observable instead of performing an actual request
   * @param serial serial ID of the scanner
   * @param data measurement that is to be processed
   * @param language currently used language
   * @param additionalData any additional data the backend might require
   * @param measurementType info about the currently running app path/measurement type
   * @returns observable for the server response
   */
  executeMeasurementHrv = (
    demoMode = false,
    serial: string,
    data: string,
    language = 'de',
    additionalData?: any,
    measurementType?: string,
  ): Observable<HttpEvent<HrvMeasurementResult>> => {

    return this.connectionService.retryRequest(
      this.apiBasicService.doMeasurementHrvSingle(serial, data, demoMode, language.substring(0, 2), additionalData, measurementType),
      1000,
      3
    );
  }

  /** Send one HRV upload request; retry policy belongs to the caller. */
  executeMeasurementHrvSingle = (
    demoMode = false,
    serial: string,
    data: string,
    language = 'de',
    additionalData?: any,
    measurementType?: string,
  ): Observable<HttpEvent<HrvMeasurementResult>> => {
    return this.apiBasicService.doMeasurementHrvSingle(
      serial, data, demoMode, language.substring(0, 2), additionalData, measurementType
    );
  }

  /**
   * upload a hrv test measurement to the server
   * this test measurement is meant to validate that the pulse rate can be measured
   * if the hand is pressed against the device too firmly, to little blood would be in the skin, and the result would be invalid
   * so this request contains information about whether to contain or abort the measurement
   * attempt to send the data up to 4 times
   * @param demoMode if true, return a mock-observable instead of performing an actual request
   * @param serial serial ID of the scanner
   * @param data measurement that is to be processed
   * @param language currently used language
   * @param additionalData any additional data the backend might require
   * @returns observable for the server response
   */
  executeMeasurementHrvTest = (
    demoMode = false,
    serial: string,
    data: string,
    language = 'de',
    additionalData?: any
  ): Observable<HrvTestMeasurementResult> => {
    return this.connectionService.retryRequest(
      this.apiBasicService.doMeasurementHrvTestSingle(serial, data, demoMode, language.substring(0, 2), additionalData),
      1000,
      3
    );
  }
}
